/**
 * Hook useMe — récupère l'utilisateur courant (status auth).
 *
 * Si non auth → erreur 401 → retourne null (le sidebar affichera "non connecté").
 * Si auth → retourne { email, name, role, ... } pour affichage du statut.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from './api.js';

export interface MeUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  createdAt: string;
}

interface MeResponse {
  user: MeUser;
  entites: unknown[];
}

export function useMe(): { user: MeUser | null; isLoading: boolean; isAuthed: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['v2', 'me'],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/api/v1/me');
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  return {
    user: data?.user ?? null,
    isLoading,
    isAuthed: !!data?.user,
  };
}

export function useLogout(): { logout: () => Promise<void>; isPending: boolean } {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.post('/api/v1/auth/logout', {}),
    onSuccess: () => {
      queryClient.clear();
      // Redirige vers la page de login (V1 pour le moment, page magic link)
      window.location.href = 'https://cockpit.andalous-elearning.com/login';
    },
  });
  return {
    logout: async () => {
      await mutation.mutateAsync();
    },
    isPending: mutation.isPending,
  };
}
